import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LogOut, Package, CalendarCheck, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { label: 'In attesa', cls: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Pagato', cls: 'bg-green-100 text-green-700' },
  confirmed: { label: 'Confermato', cls: 'bg-green-100 text-green-700' },
  shipped: { label: 'Spedito', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completato', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annullato', cls: 'bg-red-100 text-red-600' },
  awaiting_payment: { label: 'In attesa di pagamento', cls: 'bg-amber-100 text-amber-700' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${s.cls}`}>{s.label}</span>;
};

export default function AccountPage() {
  const { user, loading, login, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [o, b] = await Promise.all([
          axios.get(`${API}/my/orders`, { withCredentials: true }),
          axios.get(`${API}/my/bookings`, { withCredentials: true }),
        ]);
        setOrders(o.data);
        setBookings(b.data);
      } catch (e) {
        console.error('Failed to load account data', e);
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#0288D1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <div className="w-20 h-20 rounded-full bg-[#FFF8F5] flex items-center justify-center mb-6">
          <User size={36} className="text-[#FF7043]" />
        </div>
        <h1 className="font-heading text-3xl font-black text-slate-900 mb-2">La tua Area Clienti</h1>
        <p className="text-slate-500 mb-8 max-w-md">
          Accedi con Google per vedere i tuoi ordini, le prenotazioni delle lezioni e velocizzare il checkout.
        </p>
        <button
          data-testid="account-login-btn"
          onClick={login}
          className="inline-flex items-center gap-3 px-7 py-3.5 bg-white border-2 border-slate-200 hover:border-[#0288D1] font-heading font-bold text-slate-700 rounded-full shadow-md transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
          Accedi con Google
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="relative bg-[#0288D1] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-white" />
          <div className="absolute bottom-10 right-20 w-60 h-60 rounded-full bg-[#FFCA28]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16 flex flex-col sm:flex-row items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-20 h-20 rounded-full border-4 border-white/30 object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center"><User size={32} className="text-white" /></div>
          )}
          <div className="text-center sm:text-left flex-1">
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-white" data-testid="account-user-name">Ciao, {user.name}!</h1>
            <p className="text-blue-100 mt-1">{user.email}</p>
          </div>
          <button
            data-testid="account-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 hover:bg-white/25 border border-white/25 text-white font-heading font-bold text-sm rounded-full transition-colors"
          >
            <LogOut size={16} /> Esci
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 space-y-10">
        <section>
          <h2 className="font-heading text-lg md:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Package size={20} className="text-[#FF7043]" /> I miei Ordini
          </h2>
          {dataLoading ? (
            <p className="text-slate-400 text-sm">Caricamento...</p>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-slate-400 text-sm" data-testid="account-no-orders">
              Nessun ordine con l'email {user.email}. Vai allo <a href="/shop" className="text-[#0288D1] underline">Shop</a>!
            </div>
          ) : (
            <div className="space-y-3" data-testid="account-orders-list">
              {orders.map(o => (
                <div key={o.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="font-heading font-bold text-slate-900">Ordine #{o.id.slice(0, 8)}</p>
                    <p className="text-sm text-slate-500">
                      {(o.items || []).map(i => `${(i.product_name || '').replace(' Caribbean Blue Moon', '')}${i.size ? ` (${i.size})` : ''} x${i.quantity}`).join(', ')}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{o.created_at ? new Date(o.created_at).toLocaleDateString('it-IT') : ''} · {o.payment_method === 'bonifico' ? 'Bonifico' : 'Carta'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-black text-[#FF7043]">€{(o.total || 0).toFixed(2)}</span>
                    <StatusBadge status={o.payment_status || o.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-heading text-lg md:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarCheck size={20} className="text-[#0288D1]" /> Le mie Prenotazioni
          </h2>
          {dataLoading ? (
            <p className="text-slate-400 text-sm">Caricamento...</p>
          ) : bookings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-slate-400 text-sm" data-testid="account-no-bookings">
              Nessuna prenotazione con l'email {user.email}. Prenota una <a href="/lezioni-prova" className="text-[#0288D1] underline">lezione di prova gratuita</a>!
            </div>
          ) : (
            <div className="space-y-3" data-testid="account-bookings-list">
              {bookings.map(b => (
                <div key={b.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="font-heading font-bold text-slate-900">{b.course_name}</p>
                    <p className="text-sm text-slate-500">Studente: {b.student_name}</p>
                    <p className="text-xs text-slate-400 mt-1">{b.created_at ? new Date(b.created_at).toLocaleDateString('it-IT') : ''}</p>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
